// Load/save helpers for the Spine setup state (user_settings.spine JSONB).
// All spine screens go through these — merge-write so parallel steps never
// clobber each other's sections.
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Gear, Sector } from '../domain/olmps';
import {
  EMPTY_SPINE,
  type GearIntakeResult,
  type SpineSectorSetup,
  type SpineState,
} from './spine-types';

export async function loadSpine(supabase: SupabaseClient, userId: string): Promise<SpineState> {
  const { data } = await supabase.from('user_settings').select('spine').eq('user_id', userId).maybeSingle();
  const raw = (data as { spine?: unknown } | null)?.spine;
  if (!raw || typeof raw !== 'object') return { ...EMPTY_SPINE, telos: {}, moyri: {} };
  const s = raw as Partial<SpineState>;
  return {
    telos: s.telos ?? {},
    moyri: s.moyri ?? {},
    kyrs: s.kyrs ?? { shapesListId: null, done: false },
    tkne: s.tkne ?? { done: false },
    completedAt: s.completedAt ?? null,
  };
}

async function writeSpine(supabase: SupabaseClient, userId: string, next: SpineState): Promise<void> {
  await supabase.from('user_settings').update({ spine: next }).eq('user_id', userId);
}

/** Read-modify-write with the given mutation applied to the freshest state. */
export async function updateSpine(
  supabase: SupabaseClient,
  userId: string,
  mutate: (s: SpineState) => SpineState
): Promise<SpineState> {
  const current = await loadSpine(supabase, userId);
  const next = mutate(current);
  await writeSpine(supabase, userId, next);
  return next;
}

export async function saveSectorSetup(
  supabase: SupabaseClient,
  userId: string,
  sector: Sector,
  setup: SpineSectorSetup
): Promise<SpineState> {
  return updateSpine(supabase, userId, (s) => ({ ...s, telos: { ...s.telos, [sector]: setup } }));
}

export async function saveGearIntake(
  supabase: SupabaseClient,
  userId: string,
  gear: Gear,
  result: GearIntakeResult
): Promise<SpineState> {
  return updateSpine(supabase, userId, (s) => ({ ...s, moyri: { ...s.moyri, [gear]: result } }));
}

export async function saveKyrsDone(
  supabase: SupabaseClient,
  userId: string,
  shapesListId: string | null
): Promise<SpineState> {
  return updateSpine(supabase, userId, (s) => ({ ...s, kyrs: { shapesListId, done: true } }));
}

export async function saveTkneDone(supabase: SupabaseClient, userId: string): Promise<SpineState> {
  return updateSpine(supabase, userId, (s) => {
    const next: SpineState = { ...s, tkne: { done: true } };
    // TKNE is the last setup step — stamp completion when everything is done.
    if (!next.completedAt) next.completedAt = new Date().toISOString();
    return next;
  });
}
